import type { ReactNode } from 'react';

import { disabled, hidden } from './availability';
import type {
  ActionAvailability,
  ActionTargets,
  EntityAction,
  EntityActionBinding,
  EntityActionDefinition,
} from './types';

const toTargetArray = <TTarget>(
  targets: ActionTargets<TTarget>,
): readonly TTarget[] =>
  Array.isArray(targets)
    ? (targets as readonly TTarget[])
    : [targets as TTarget];

type EntityActionCore<TTarget, TArgs extends unknown[]> = {
  id: string;
  label: string;
  getAvailability: (targets: ActionTargets<TTarget>) => ActionAvailability;
  getIsPending: (targets?: ActionTargets<TTarget>) => boolean;
  run: (targets: readonly TTarget[], ...args: TArgs) => void;
};

/** Derives every getter from availability and pending, and guards `execute`. */
const deriveEntityAction = <TTarget, TArgs extends unknown[]>({
  id,
  label,
  getAvailability,
  getIsPending,
  run,
}: EntityActionCore<TTarget, TArgs>): EntityAction<TTarget, TArgs> => {
  const getCanExecute = (targets: ActionTargets<TTarget>) =>
    getAvailability(targets).status === 'enabled' && !getIsPending(targets);

  return {
    id,
    label,
    getAvailability,
    getIsVisible: (targets) => getAvailability(targets).status !== 'hidden',
    getCanExecute,
    getDisabledReason: (targets) => {
      const availability = getAvailability(targets);
      return availability.status === 'disabled'
        ? availability.reason
        : undefined;
    },
    getIsPending,
    execute: (targets, ...args) => {
      if (getCanExecute(targets)) {
        run(toTargetArray(targets), ...args);
      }
    },
  };
};

/**
 * Binds a pure definition to a page's context and effects. Composition roots
 * call this; consumers only ever see the resulting action.
 */
export const createEntityAction = <
  TTarget,
  TContext,
  TArgs extends unknown[] = [],
  TExtra extends object = object,
>(
  definition: EntityActionDefinition<TTarget, TContext>,
  ctx: TContext,
  binding: EntityActionBinding<TTarget, TArgs>,
  extra?: TExtra,
): EntityAction<TTarget, TArgs, TExtra> => {
  const getAvailability = (
    targets: ActionTargets<TTarget>,
  ): ActionAvailability => {
    const targetArray = toTargetArray(targets);
    if (targetArray.length === 0) {
      return hidden('No targets');
    }

    let availability: ActionAvailability;
    if (definition.selection === 'single') {
      if (targetArray.length > 1) {
        return hidden(`${definition.id} acts on one target at a time`);
      }
      availability = definition.decide(targetArray[0] as TTarget, ctx);
    } else {
      availability = definition.decide(targetArray, ctx);
    }

    if (availability.status !== 'enabled') {
      return availability;
    }
    const transientReason = binding.getTransientReason?.(targetArray);
    return transientReason ? disabled(transientReason) : availability;
  };

  const getIsPending = (targets?: ActionTargets<TTarget>) =>
    binding.getIsPending?.(
      targets === undefined ? undefined : toTargetArray(targets),
    ) ?? false;

  return {
    ...extra,
    ...deriveEntityAction({
      id: definition.id,
      label: definition.label,
      getAvailability,
      getIsPending,
      run: binding.execute,
    }),
  } as EntityAction<TTarget, TArgs, TExtra>;
};

/** Always hidden and never executes. Used as the fallback when no provider is mounted. */
export const hiddenAction = <
  TTarget,
  TArgs extends unknown[] = [],
  TExtra extends object = object,
>(
  { id, label }: { id: string; label: string },
  extra?: TExtra,
): EntityAction<TTarget, TArgs, TExtra> =>
  ({
    ...extra,
    ...deriveEntityAction<TTarget, TArgs>({
      id,
      label,
      getAvailability: () => hidden('No entity actions provider is mounted'),
      getIsPending: () => false,
      run: () => undefined,
    }),
  } as EntityAction<TTarget, TArgs, TExtra>);

/**
 * Applies a reason that only this screen knows about on top of an entity
 * action. If the entity policy already hid or disabled the action, its
 * reason wins and the local reason is ignored.
 */
export const withLocalDisabled = <
  TTarget,
  TArgs extends unknown[] = [],
  TExtra extends object = object,
>(
  action: EntityAction<TTarget, TArgs, TExtra>,
  localReason: ReactNode,
): EntityAction<TTarget, TArgs, TExtra> => {
  if (!localReason) {
    return action;
  }
  return {
    ...action,
    ...deriveEntityAction<TTarget, TArgs>({
      id: action.id,
      label: action.label,
      getAvailability: (targets) => {
        const availability = action.getAvailability(targets);
        return availability.status === 'enabled'
          ? disabled(localReason)
          : availability;
      },
      getIsPending: action.getIsPending,
      run: (targets, ...args) => action.execute(targets, ...args),
    }),
  };
};

export const hasVisibleAction = <TTarget>(
  actions: Record<string, Pick<EntityAction<TTarget>, 'getIsVisible'>>,
  targets: ActionTargets<TTarget>,
): boolean =>
  Object.values(actions).some((action) => action.getIsVisible(targets));
